import React, { useMemo, useEffect, useState } from 'react';
import { AlertTriangle, Archive } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';

const DEFAULT_THRESHOLD_DAYS = 60;
// Warn the manager at 50% of the threshold — heads-up before auto-archive.
const WARN_RATIO = 0.5;

/**
 * Warns the manager when a report has been sitting in AGUARDA_INFO for
 * more than the configured threshold, suggesting closure by lack of
 * response from the reporter. Threshold is read from the platform
 * public settings so it stays aligned with the auto-archive job.
 */
const StaleAwaitingInfoAlert = ({ report, onSuggestArchive }) => {
  const [thresholdDays, setThresholdDays] = useState(DEFAULT_THRESHOLD_DAYS);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get('/admin/platform-public-settings');
        if (!alive) return;
        const v = Number(data.auto_archive_awaiting_info_days);
        if (v && v > 0) setThresholdDays(v);
      } catch {
        // fallback to default
      }
    })();
    return () => { alive = false; };
  }, []);

  const daysSinceLastActivity = useMemo(() => {
    if (!report || report.status !== 'AGUARDA_INFO') return 0;
    const lastReporterMsg = (report.messages || [])
      .filter((m) => m.sender_type === 'reporter')
      .map((m) => new Date(m.created_at).getTime())
      .reduce((a, b) => Math.max(a, b), 0);
    const updatedAt = report.updated_at ? new Date(report.updated_at).getTime() : 0;
    const anchor = Math.max(lastReporterMsg, updatedAt);
    if (!anchor) return 0;
    return Math.floor((Date.now() - anchor) / (1000 * 60 * 60 * 24));
  }, [report]);

  if (report?.status !== 'AGUARDA_INFO') return null;
  const warnAt = Math.max(1, Math.floor(thresholdDays * WARN_RATIO));
  if (daysSinceLastActivity < warnAt) return null;

  const isPastThreshold = daysSinceLastActivity >= thresholdDays;
  const suggestedText =
    `A sua denúncia esteve em espera de informação adicional durante ${daysSinceLastActivity} dias. ` +
    `Sem resposta atempada, é arquivada por inatividade (limite: ${thresholdDays} dias). ` +
    'Se pretender retomar o processo, pode responder por este canal e o gestor reabrirá o caso.';

  return (
    <Alert className={isPastThreshold ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'} data-testid="stale-awaiting-info-alert">
      <AlertTriangle className={`w-4 h-4 ${isPastThreshold ? 'text-red-600' : 'text-amber-600'}`} />
      <AlertDescription>
        <div className="text-sm">
          <div className={`font-medium ${isPastThreshold ? 'text-red-900' : 'text-amber-900'}`}>
            {isPastThreshold
              ? `Denúncia em AGUARDA_INFO há ${daysSinceLastActivity} dias — passou o limite de ${thresholdDays} dias`
              : `Denúncia em AGUARDA_INFO há ${daysSinceLastActivity} dias (limite ${thresholdDays} dias) — sugestão de encerramento`}
          </div>
          <div className={`mt-1 ${isPastThreshold ? 'text-red-800' : 'text-amber-800'}`}>
            O denunciante não responde há mais tempo do que o configurado. Considere <strong>arquivar por inatividade</strong>.
            {isPastThreshold && ' O job automático (Super Admin) irá arquivar esta denúncia na próxima execução.'}
          </div>
          <div className="mt-3">
            <Button
              size="sm"
              variant="outline"
              className={isPastThreshold ? 'border-red-300 text-red-800 hover:bg-red-100' : 'border-amber-300 text-amber-800 hover:bg-amber-100'}
              onClick={() => onSuggestArchive?.(suggestedText)}
              data-testid="suggest-archive-btn"
            >
              <Archive className="w-4 h-4 mr-2" />
              Arquivar por inatividade
            </Button>
          </div>
        </div>
      </AlertDescription>
    </Alert>
  );
};

export default StaleAwaitingInfoAlert;
